import { rd } from '@passionware/monads';
import { Capability, Resource } from '__types__';
import { useRestrictedAccess } from 'lib/restrictedAccess';
import moment from 'moment';
import { createSimpleEvent, type SimpleEvent } from '@passionware/simple-event';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import type { SchemaError } from 'v4/entities/common/parseWithDecoratedError';
import { useOptimisticResource } from 'v4/entities/resource/resource.optimistic';
import ResourcePaneHeader from 'v4/pages/resources/pane/ResourcePaneHeader/ResourcePaneHeader';
import { type AppScope, appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { useIsEnterpriseEnabled } from 'v5/core/app-scope/useAppRouting';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from 'v5/design-system/Tabs';
import { globalCoreUnitId } from 'v5/core/entity-schema/resource/resolveResourceSchema';
import { ResourceDetailsForm } from 'v5/features/resources/form/ResourceDetailsForm';
import { ResourceFilesPanel } from 'v5/features/resources/ResourceFilesPanel';
import { ResourceNotesPanel } from 'v5/features/resources/ResourceNotesPanel';
import { InvalidResourceNotice } from 'v5/features/resources/pane/InvalidResourceNotice';
import { ResourcePaneSkeleton } from 'v5/features/resources/pane/ResourcePaneSkeleton';
import { settleSkeletons } from 'v5/features/resources/skeletonFade';
import { ResourcePinboard } from 'v5/features/resources/pinboard/ResourcePinboard';
import {
  EntityListRowErrorBoundary,
  type EntityListRowErrorBoundaryLabels
} from 'v5/patterns/entity-list/EntityListRowErrorBoundary';
import { useAdvancedValidationRowUi } from 'v5/patterns/entity-list/useAdvancedValidationRowUi';
import { ValidatedRowInvalidRenderPanel } from 'v5/patterns/entity-list/ValidatedRowInvalidRenderPanel';
import { renderError } from 'v5/patterns/feedback/ErrorAlert';
import { SCHEMA_FORM_GUTTER } from 'v5/patterns/forms/schema-form/gutter';
import { cn } from 'v5/platform/dom/cn';
import { schemaErrorFallback } from 'v4/entities/common/schemaErrorFallback';
import { myServices } from 'v5/services/services.connected.app';
import type { UnitTarget } from 'v5/patterns/forms/schema-form/schemaForm.types';

export interface ResourcePaneProps {
  /** A saved resource; new ones are created through `ResourceCreateDialog`. */
  resource: Resource;
  hideTabs?: boolean;
  hideFullLink?: boolean;
  hideOptions?: boolean;
  isReadOnly?: boolean;
  /** Wider gutter, for full-height hosts (the contact page, the drawer). */
  padded?: boolean;
  /** The tab it opens on (a picture opens the pinboard); details by default. */
  initialTab?: 'details' | 'notes' | 'documents' | 'pinboard';
  /** The picture the pinboard opens at, scrolled into view. */
  initialPinId?: number;
  /** The host's own action over the pane's top-right corner: closing the panel it stands in. */
  cornerAction?: ReactNode;
  /** Browsing scope the header's links keep; every v5 host passes its own. */
  appScope: AppScope;
}

/**
 * Resource pane: header, the v6 details form and the Notes / Documents /
 * Pinboard tabs.
 * A resource whose stored data fails validation is handled like an invalid
 * table row: the header and tabs render from the unvalidated data; the
 * Details tab marks the issues and shows the form from that data (with
 * `low-level-validation`, only after "display anyway").
 */
export function ResourcePane({
  resource: legacyResource,
  hideTabs,
  hideFullLink,
  hideOptions,
  isReadOnly,
  padded = false,
  initialTab = 'details',
  initialPinId,
  cornerAction,
  appScope
}: ResourcePaneProps) {
  const resourceId = legacyResource.id;
  const resourceQuery = myServices.resourceService.useItem(resourceId);
  const userHasAccess = useRestrictedAccess(
    null,
    Capability.RESOURCE_UPDATE,
    null
  );
  const [tab, setTab] = useState<string>(initialTab);
  const [displayAnywayId, setDisplayAnywayId] = useState<number | null>(null);
  const { t } = useTranslation('patterns', { keyPrefix: 'EntityListRow' });
  const renderCrashLabels: EntityListRowErrorBoundaryLabels = {
    title: t('renderCrashed'),
    tryAgain: t('renderTryAgain')
  };
  // The header's name opens the form's Full name unit; heard on the Details tab.
  const editUnitSignal = useMemo(() => createSimpleEvent<UnitTarget>(), []);

  const optimisticState = useOptimisticResource({
    id: resourceId,
    entity: rd.isIdle(resourceQuery)
      ? rd.of(legacyResource)
      : schemaErrorFallback.recoverSchemaError(resourceQuery),
    executeAction: async action => {
      try {
        await myServices.resourceMutationService.updateResource({
          id: resourceId,
          data: action.payload
        });
        myServices.toastService.open({
          type: 'success',
          message: 'Resource updated successfully'
        });
      } catch (error) {
        myServices.toastService.open({
          type: 'error',
          message: 'Failed to update resource'
        });
        throw error;
      }
    }
  });

  // the resource is there: whatever loaded before it was one loading, and it is over
  const resourceLoaded = rd.tryGet(optimisticState.entity) !== undefined;
  useEffect(() => {
    if (resourceLoaded) settleSkeletons();
  }, [resourceLoaded]);

  const readOnly = !userHasAccess || !!isReadOnly;
  const invalid = schemaErrorFallback.schemaErrorOf(resourceQuery);
  const advancedValidationUi = useAdvancedValidationRowUi(
    myServices.featureService,
    myServices.appScopeService
  );
  const details = (resource: Resource) => (
    <ResourcePaneValidatedDetails
      invalid={invalid}
      advanced={advancedValidationUi}
      displayAnyway={displayAnywayId === resourceId}
      onDisplayAnywayChange={enabled =>
        setDisplayAnywayId(enabled ? resourceId : null)
      }
      renderCrashLabels={renderCrashLabels}
    >
      <ResourcePaneDetails
        resource={resource}
        readOnly={readOnly}
        editUnitSignal={editUnitSignal}
      />
    </ResourcePaneValidatedDetails>
  );

  return (
    <div className="tailwind-root contents">
      <div
        className={cn(
          'flex h-full flex-col bg-bg-default text-fg-default',
          padded ? SCHEMA_FORM_GUTTER.md : SCHEMA_FORM_GUTTER.sm,
          cornerAction && 'relative'
        )}
      >
        {cornerAction && (
          <div className="absolute top-2 right-2 z-30">{cornerAction}</div>
        )}
        <EntityListRowErrorBoundary
          key={resourceId}
          labels={renderCrashLabels}
          className="mx-(--schema-form-gutter) my-2"
        >
          {rd
            .journey(optimisticState.entity)
            .wait(<ResourcePaneSkeleton />)
            .catch(renderError)
            .map(resource => (
              <>
                <ResourcePaneHeader
                  resource={resource}
                  hideOptions={hideOptions}
                  hideFullLink={hideFullLink}
                  appScope={appScope}
                  notice={
                    invalid !== undefined && !advancedValidationUi ? (
                      <InvalidResourceNotice
                        resourceId={resourceId}
                        error={invalid}
                        services={myServices}
                        className="-mx-4 mt-2"
                      />
                    ) : undefined
                  }
                  onAction={action =>
                    optimisticState.scheduleAction(v4(), action)
                  }
                  onEditName={() =>
                    editUnitSignal.emit({
                      unitId: globalCoreUnitId('full_name'),
                      fieldPath: null
                    })
                  }
                />
                {hideTabs ? (
                  details(resource)
                ) : (
                  <Tabs
                    value={tab}
                    onValueChange={setTab}
                    className="flex min-h-0 flex-1 flex-col"
                  >
                    <TabsList
                      variant="inline"
                      className="px-(--schema-form-gutter)"
                    >
                      <TabsTrigger value="details">Details</TabsTrigger>
                      <TabsTrigger value="notes">Notes</TabsTrigger>
                      <TabsTrigger value="documents">Documents</TabsTrigger>
                      <TabsTrigger value="pinboard">Pinboard</TabsTrigger>
                    </TabsList>
                    <TabsContent
                      value="details"
                      className="flex min-h-0 flex-1 flex-col"
                    >
                      {details(resource)}
                    </TabsContent>
                    <TabsContent
                      value="notes"
                      className="min-h-0 flex-1 overflow-y-auto"
                    >
                      <ResourceNotesPanel
                        services={myServices}
                        resource={resource}
                        disabled={readOnly}
                      />
                    </TabsContent>
                    <TabsContent
                      value="documents"
                      className="flex min-h-0 flex-1 flex-col"
                    >
                      <ResourceFilesPanel
                        services={myServices}
                        resource={resource}
                        disabled={readOnly}
                      />
                    </TabsContent>
                    <TabsContent
                      value="pinboard"
                      className="flex min-h-0 flex-1 flex-col"
                    >
                      <ResourcePinboard
                        services={myServices}
                        resourceId={resource.id}
                        readOnly={readOnly}
                        initialPinId={initialPinId}
                      />
                    </TabsContent>
                  </Tabs>
                )}
              </>
            ))}
        </EntityListRowErrorBoundary>
      </div>
    </div>
  );
}

/**
 * The Details tab for a resource that may have failed validation, like an
 * invalid table row (`SortableEntityTable`): the details render from the
 * unvalidated data in their own boundary; with `low-level-validation`, only
 * below the red panel and after "display anyway". The tree stays the same
 * when the data turns valid or invalid, so the form keeps its state.
 */
function ResourcePaneValidatedDetails({
  invalid,
  advanced,
  displayAnyway,
  onDisplayAnywayChange,
  renderCrashLabels,
  children
}: {
  invalid: SchemaError | undefined;
  /** `low-level-validation`: the red panel with "display anyway", as in the table. */
  advanced: boolean;
  displayAnyway: boolean;
  onDisplayAnywayChange: (enabled: boolean) => void;
  renderCrashLabels: EntityListRowErrorBoundaryLabels;
  children: ReactNode;
}) {
  const panel = advanced && invalid !== undefined;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {panel && (
        <div className="px-(--schema-form-gutter) pt-2">
          <ValidatedRowInvalidRenderPanel<Resource>
            error={invalid.innerError}
            rawItem={invalid.data}
            renderItem={() => null}
            separatePreviewSurface
            unsafePreview={displayAnyway}
            onUnsafePreviewChange={onDisplayAnywayChange}
          />
        </div>
      )}
      {(!panel || displayAnyway) && (
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col',
            panel && 'bg-bg-danger/25'
          )}
        >
          <EntityListRowErrorBoundary
            labels={renderCrashLabels}
            className="mx-(--schema-form-gutter) my-2"
          >
            {children}
          </EntityListRowErrorBoundary>
        </div>
      )}
    </div>
  );
}

function ResourcePaneDetails({
  resource,
  readOnly,
  editUnitSignal
}: {
  resource: Resource;
  readOnly: boolean;
  editUnitSignal: SimpleEvent<UnitTarget>;
}) {
  // Without the enterprise flag nothing shows workspaces while browsing the
  // organization; the form then shows the Global section alone, bare.
  const enterprise = useIsEnterpriseEnabled();
  const browsingOrganization = rd.mapOrElse(
    myServices.appScopeService.useResolvedAppScope(),
    resolved =>
      appScopeUtils.identityFromResolved(resolved).level === 'organization',
    true
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* The form fills the space above the meta line and scrolls itself below its section tabs. */}
      <div className="flex min-h-0 flex-1 flex-col">
        <ResourceDetailsForm
          resourceId={resource.id}
          forceReadOnly={readOnly}
          workspaceSections={enterprise || !browsingOrganization}
          services={myServices}
          editUnitSignal={editUnitSignal}
        />
      </div>
      <div className="px-(--schema-form-gutter) py-1 text-xs text-fg-muted">
        <span title={moment(resource.updated_at).format('MM/DD/YYYY HH:mm:ss')}>
          Updated {moment(resource.updated_at).fromNow()}
        </span>
        {' · '}
        <span title={moment(resource.created_at).format('MM/DD/YYYY HH:mm:ss')}>
          Created {moment(resource.created_at).fromNow()}
        </span>
      </div>
    </div>
  );
}
